import { useState } from 'react'
import { useParams } from 'react-router-dom'
import {
  Stack, Text, Textarea, Button, Paper, Title,
  Center, Rating, Alert, Box,
} from '@mantine/core'
import { IconCheck, IconAlertCircle } from '@tabler/icons-react'
import { useMutation } from '@tanstack/react-query'
import { api } from '@/api/client'

type CsatError = 'token_csat_invalido' | 'csat_ja_submetido' | 'token_csat_expirado'

const ERROR_MESSAGES: Record<CsatError, string> = {
  token_csat_invalido: 'Token de avaliação inválido.',
  csat_ja_submetido: 'Esta avaliação já foi enviada anteriormente.',
  token_csat_expirado: 'O prazo para avaliação expirou.',
}

export function CsatForm() {
  const { token } = useParams<{ token: string }>()
  const [rating, setRating] = useState(0)
  const [comment, setComment] = useState('')
  const [submitted, setSubmitted] = useState(false)
  const [errorCode, setErrorCode] = useState<CsatError | null>(null)

  const mutation = useMutation({
    mutationFn: async (payload: { rating: number; comment: string }) => {
      await api.post(`/v1/csat/${token}`, payload)
    },
    onSuccess: () => {
      setSubmitted(true)
    },
    onError: (err: any) => {
      const code = err?.response?.data?.code as CsatError | undefined
      if (code && code in ERROR_MESSAGES) {
        setErrorCode(code)
      } else {
        setErrorCode(null)
      }
    },
  })

  const handleSubmit = () => {
    if (rating === 0) return
    mutation.mutate({ rating, comment: comment.trim() })
  }

  return (
    <Center mih="100vh" bg="gray.0" p="md">
      <Paper shadow="md" p="xl" radius="md" maw={480} w="100%" withBorder>
        <Stack gap="lg" align="center">
          <Box>
            <Title order={3} ta="center" c="blue.7">ConsoleAI</Title>
            <Text size="sm" c="dimmed" ta="center">Avaliação de atendimento</Text>
          </Box>

          {submitted ? (
            <Stack align="center" gap="md" py="xl" role="status" aria-label="Avaliação enviada com sucesso">
              <IconCheck size={48} color="var(--mantine-color-teal-6)" />
              <Title order={4} ta="center">Obrigado pela sua avaliação!</Title>
              <Text size="sm" c="dimmed" ta="center">
                Sua opinião nos ajuda a melhorar o atendimento.
              </Text>
            </Stack>
          ) : errorCode ? (
            <Alert
              icon={<IconAlertCircle size={16} />}
              color="red"
              variant="light"
              title="Avaliação indisponível"
              w="100%"
            >
              {ERROR_MESSAGES[errorCode]}
            </Alert>
          ) : (
            <>
              {mutation.isError && !errorCode && (
                <Alert
                  icon={<IconAlertCircle size={16} />}
                  color="red"
                  variant="light"
                  w="100%"
                >
                  Ocorreu um erro ao enviar. Tente novamente.
                </Alert>
              )}

              <Stack align="center" gap="xs">
                <Text size="sm" fw={500}>Como você avalia o atendimento?</Text>
                {/*
                  `role="radiogroup"` no contêiner, e não no `Rating`.

                  O Mantine 7.17.8 renderiza as estrelas como inputs de rádio com
                  `aria-label="1".."5"` e NÃO emite contêiner com papel de grupo — conferido
                  no DOM. Sem o papel, o `aria-label` abaixo fica pendurado num elemento que
                  o leitor de tela não anuncia como grupo, e a pessoa ouve cinco rádios
                  soltos chamados "1".."5", sem saber do que são.
                */}
                <Box role="radiogroup" aria-label="Nota de avaliação de 1 a 5 estrelas">
                  <Rating size="xl" value={rating} onChange={setRating} />
                </Box>
              </Stack>

              <Textarea
                label="Comentário (opcional)"
                placeholder="Conte-nos mais sobre sua experiência..."
                rows={3}
                w="100%"
                value={comment}
                onChange={(e) => setComment(e.target.value)}
              />

              <Button
                onClick={handleSubmit}
                loading={mutation.isPending}
                disabled={rating === 0}
                fullWidth
              >
                Enviar avaliação
              </Button>
            </>
          )}
        </Stack>
      </Paper>
    </Center>
  )
}
